export class NookApiError extends Error {
    status;
    code;
    /**
     * The `x-request-id` the backend echoed. It is the join key between this
     * error, an Axiom log line and a Sentry event, so it is carried on the error
     * rather than logged and dropped.
     */
    requestId;
    constructor(status, code, message, requestId) {
        super(message);
        this.name = "NookApiError";
        this.status = status;
        this.code = code;
        this.requestId = requestId;
    }
}
/**
 * `402 plan_expired` (block 20). Mapped here, once, so every gated call —
 * `useCreateNook`, `useAskStream`, regenerate — inherits it. Keyed off the code
 * as well as the status (decision register C2): a paying user out of storage
 * must never be shown the trial-ended prompt.
 */
export class PlanExpiredError extends NookApiError {
    upgradeUrl;
    constructor(message, upgradeUrl, requestId) {
        super(402, "plan_expired", message, requestId);
        this.name = "PlanExpiredError";
        this.upgradeUrl = upgradeUrl;
    }
}
export function createHttpClient(config) {
    const baseUrl = config.baseUrl.replace(/\/+$/, "");
    async function send({ path, method = "GET", body, signal, headers: extra }, accept) {
        const requestId = crypto.randomUUID();
        const headers = new Headers({
            accept,
            // Honoured by the backend's `requestId` middleware when it matches its
            // safe-character pattern, and echoed back on the response.
            "x-request-id": requestId,
            "x-nook-surface": config.surface,
            ...extra,
        });
        const token = await config.authTokenProvider();
        if (token)
            headers.set("authorization", `Bearer ${token}`);
        if (body !== undefined)
            headers.set("content-type", "application/json");
        // Read per call, and called unbound: a browser's `fetch` throws as `config.fetch()`.
        const response = await (config.fetch ?? fetch)(`${baseUrl}${path}`, {
            method,
            headers,
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            ...(signal ? { signal } : {}),
        });
        if (!response.ok) {
            const failure = await readErrorBody(response);
            const echoedId = response.headers.get("x-request-id") ?? requestId;
            if (response.status === 402 && failure?.error.code === "plan_expired") {
                throw new PlanExpiredError(failure.error.message, String(failure.error.upgradeUrl), echoedId);
            }
            const error = new NookApiError(response.status, failure?.error.code ?? "UNKNOWN_ERROR", failure?.error.message ?? `Request failed (${response.status})`, echoedId);
            if (token && response.status === 401)
                config.onUnauthorized?.(error);
            throw error;
        }
        return response;
    }
    return {
        async request(options) {
            const response = await send(options, "application/json");
            // A `204` has no body to read — `response.json()` on one throws. Handling
            // it here rather than in the endpoint is the same call as the rest of
            // this file: it is HTTP, not something `DELETE /v1/nooks/:id` knows.
            if (response.status === 204)
                return options.schema.parse(undefined);
            // Parsed, not cast. `@nook/schema` is the source of truth for the shape,
            // so a backend that drifts fails loudly here rather than as `undefined`
            // three components later.
            return options.schema.parse(await response.json());
        },
        async *stream(options) {
            const response = await send(options, "text/event-stream");
            if (!response.body)
                return;
            // `TextDecoder` with `stream: true` rather than `TextDecoderStream`, which
            // Hermes lacks. A multi-byte character split across chunks decodes whole.
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            try {
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done)
                        return;
                    buffer += decoder.decode(value, { stream: true });
                    const frames = buffer.split("\n\n");
                    buffer = frames.pop() ?? "";
                    for (const frame of frames) {
                        const data = frame
                            .split("\n")
                            .filter((line) => line.startsWith("data: "))
                            .map((line) => line.slice(6))
                            .join("\n");
                        if (data)
                            yield options.schema.parse(JSON.parse(data));
                    }
                }
            }
            finally {
                reader.releaseLock();
            }
        },
    };
}
/**
 * An error body that is missing, empty, or not JSON is not itself an error —
 * a 502 from a proxy has no Nook envelope in it — so a failure to parse falls
 * back to the status code rather than masking the original failure.
 */
async function readErrorBody(response) {
    try {
        const value = await response.json();
        return isErrorBody(value) ? value : null;
    }
    catch {
        return null;
    }
}
function isErrorBody(value) {
    if (typeof value !== "object" || value === null || !("error" in value))
        return false;
    const error = value.error;
    return (typeof error === "object" &&
        error !== null &&
        "code" in error &&
        "message" in error &&
        typeof error.code === "string" &&
        typeof error.message === "string");
}
//# sourceMappingURL=http.js.map